/**
 * Centralized Supabase Configuration
 *
 * Single source of truth for all Supabase-related URLs and keys.
 * To switch between custom domain and direct URL, just change the
 * environment variables or toggle the USE_CUSTOM_DOMAIN flag.
 */

import { createScopedLogger } from '../utils/logger.js';

const log = createScopedLogger('supabase-config');
const shouldLogConfig = (() => {
  const envLevel = import.meta.env.VITE_LOG_LEVEL?.toLowerCase?.();
  const explicitDebug = import.meta.env.VITE_DEBUG_LOGS === 'true';
  return import.meta.env.DEV && (envLevel === 'debug' || explicitDebug);
})();

// Toggle this to easily switch between custom domain and direct URL
// When true: Uses api.opendoorvalet.com (branded URL)
// When false: Uses bmmfmmdpphbndezgugtx.supabase.co (direct URL)
const USE_CUSTOM_DOMAIN = true; // api.opendoorvalet.com verified live 2026-05-21

// Production URLs
const DIRECT_URL = 'https://bmmfmmdpphbndezgugtx.supabase.co';
const CUSTOM_URL = 'https://api.opendoorvalet.com';

// OAuth redirect URLs - MUST match what's configured in Supabase dashboard
const DIRECT_REDIRECT_URL = 'https://bmmfmmdpphbndezgugtx.supabase.co';
const CUSTOM_REDIRECT_URL = 'https://api.opendoorvalet.com';
const TEST_REDIRECT_URL = 'https://test.opendoorvalet.com';

const IS_BROWSER = typeof window !== 'undefined';

// Development redirect URL (falls back to env when running in Node)
const LOCAL_REDIRECT_URL = IS_BROWSER
  ? window.location.origin
  : process.env.SUPABASE_LOCAL_REDIRECT_URL || 'http://localhost:5173';

/**
 * Determine which URL to use based on configuration
 */
const getSupabaseUrl = () => {
  // Check environment variable first (highest priority)
  const envUrl = import.meta.env.VITE_SUPABASE_URL;
  if (envUrl) return envUrl;

  // Otherwise use the toggle flag
  return USE_CUSTOM_DOMAIN ? CUSTOM_URL : DIRECT_URL;
};

/**
 * Get the appropriate redirect URL based on the API URL being used
 * 
 * IMPORTANT: This is the URL where users land AFTER OAuth completes.
 * It should be the frontend app URL, NOT the Supabase API URL.
 * 
 * The OAuth flow:
 * 1. User clicks "Sign in" → redirects to OAuth provider (Google/Discord)
 * 2. OAuth provider → redirects to Supabase callback: {SUPABASE_URL}/auth/v1/callback
 * 3. Supabase processes OAuth → redirects to this redirectTo URL (frontend app)
 */
const getRedirectUrl = () => {
  const currentOrigin = IS_BROWSER
    ? window.location.origin
    : (process.env.SUPABASE_REDIRECT_URL || LOCAL_REDIRECT_URL);

  // Always use current origin for local dev and test.opendoorvalet.com
  if (currentOrigin.includes('localhost') ||
      currentOrigin.includes('test.opendoorvalet.com')) {
    return currentOrigin;
  }

  // In production, use the frontend app URL (where users should land after OAuth)
  // Default to /auth route for OAuth callbacks
  // Individual OAuth calls can override this with their own redirectTo path
  return currentOrigin;
};

/**
 * Main Supabase configuration object
 * Import this in all files that need Supabase config
 */
export const SUPABASE_CONFIG = {
  // API URLs
  url: getSupabaseUrl(),
  functionsUrl: DIRECT_URL, // Always use direct URL for functions

  // API Keys - JWT format required
  // SECURITY: anonKey is safe to hardcode (public key protected by RLS)
  anonKey: import.meta.env.VITE_SUPABASE_ANON_KEY ||
    'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImJtbWZtbWRwcGhibmRlemd1Z3R4Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTUxNzEzMzAsImV4cCI6MjA3MDc0NzMzMH0.esC_sPd7Hs79OmGPBMPxQRcB-bSFB4OV_x-B0wmFZCM',

  // SECURITY: serviceRoleKey should NEVER be in browser!
  // It's only needed for Edge Functions/server-side code which get it from Supabase secrets
  // Browser code uses getSupabaseAdmin() which returns regular client with RLS protection
  // Never read a VITE_* name here: Vite inlines VITE_* into the browser bundle.
  serviceRoleKey: process.env?.SUPABASE_SERVICE_ROLE_KEY ||
    '', // Empty string - will be caught by validation in server code

  // OAuth Configuration
  redirectUrl: getRedirectUrl(),

  // Project metadata
  project: 'bmmfmmdpphbndezgugtx',
  dashboard: 'https://supabase.com/dashboard/project/bmmfmmdpphbndezgugtx',

  // Feature flags
  useCustomDomain: USE_CUSTOM_DOMAIN,
  isDevelopment: import.meta.env.DEV || false,
  isProduction: !import.meta.env.DEV,
};

/**
 * Helper function to get auth config for Supabase client
 */
/**
 * Custom lock function that bypasses navigator.locks in development
 * to prevent deadlocks caused by React StrictMode and HMR
 *
 * The lock function receives (name, acquireTimeout, callback) and should:
 * 1. Acquire a lock with the given name
 * 2. Execute the callback
 * 3. Release the lock
 *
 * In development, we skip the lock entirely and just run the callback
 */
const noOpLock = async (name, acquireTimeout, callback) => {
  // Bypass navigator.locks entirely - just execute the callback
  // This prevents deadlocks caused by HMR, PWA service workers, and Safari bugs
  return await callback();
};

export const getAuthConfig = () => {
  const IS_BROWSER = typeof window !== 'undefined';

  // ALWAYS enable detectSessionInUrl in browser
  // The Supabase client is a singleton that persists across navigation,
  // so we can't conditionally enable this based on URL at creation time.
  // Supabase handles duplicate code exchange attempts gracefully.
  return {
    persistSession: true,
    autoRefreshToken: true,
    // Always enabled - Supabase handles OAuth code exchange automatically
    detectSessionInUrl: true,
    storage: IS_BROWSER ? window.localStorage : undefined,
    storageKey: 'odv-supabase-auth',
    flowType: 'pkce', // Required for Google OAuth
    // Use the configured redirect URL
    redirectTo: SUPABASE_CONFIG.redirectUrl,
    // ✅ Disable navigator.locks to prevent deadlocks
    // The lock mechanism causes issues in multiple scenarios:
    // - React StrictMode and HMR in development
    // - PWA service workers in production
    // - Multiple browser tabs with same session
    // - Safari's incomplete navigator.locks implementation
    // By using noOpLock everywhere, we rely on Supabase's internal token refresh
    // logic which is already designed to handle concurrent access safely.
    lock: noOpLock,
  };
};

// Minimal auth config for queries (no session auto-restoration)
// Uses a different storage key to avoid duplicate GoTrueClient warnings
export const getMinimalAuthConfig = () => ({
  persistSession: false,
  autoRefreshToken: false,
  detectSessionInUrl: false,
  storage: undefined,
  storageKey: 'odv-data-session', // Different key from auth client to avoid conflicts
});

// Development logging - only when explicit debug logging enabled
if (shouldLogConfig) {
  const payload = {
    url: SUPABASE_CONFIG.url,
    redirectUrl: SUPABASE_CONFIG.redirectUrl,
    useCustomDomain: SUPABASE_CONFIG.useCustomDomain,
    project: SUPABASE_CONFIG.project,
  };

  if (IS_BROWSER) {
    log.debug?.('Supabase config loaded (browser)', {
      ...payload,
      currentOrigin: window.location.origin,
    });
  } else {
    log.debug?.('Supabase config loaded (server)', payload);
  }
}

export default SUPABASE_CONFIG;
